import React, { useState } from 'react';
import { Box, Button, Dialog, IconButton, Paper, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CloseIcon from '@mui/icons-material/Close';
import { tok } from '../theme/businessTheme';

interface Props {
  questions: Record<string, Record<string, string>>;
  submitted: boolean;
  title?: string;
  busy?: boolean;
  onRequestAttention?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

/** A customer receipt. Rendering and expanding it never submits or changes an answer. */
export default function SubmissionReceipt({ questions, submitted, title, busy, onRequestAttention }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [photo, setPhoto] = useState<string | null>(null);
  const sections = ['Required', 'Optional', ...Object.keys(questions).filter(s => s !== 'Required' && s !== 'Optional').sort()];
  const entries = sections.flatMap(section => Object.keys(questions[section] || {}).sort().map(question => ({
    id: `${section}/${question}`, question, answer: questions[section][question],
  })));

  return (
    <Paper elevation={0} sx={{ width: '100%', maxWidth: 440, minWidth: 0, p: 2.5, borderRadius: '24px',
      bgcolor: tok('card'), color: tok('onSurface'), border: `1px solid ${tok('cardBorder')}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.25 }}>
        <Box sx={{ width: 44, height: 44, flexShrink: 0, display: 'grid', placeItems: 'center',
          borderRadius: '14px', bgcolor: tok('tile'), color: tok('onTile') }}>
          {submitted ? <CheckCircleOutlineIcon /> : <DescriptionOutlinedIcon />}
        </Box>
        <Box>
          <Typography sx={{ fontFamily: '"DM Serif Display", serif', fontSize: '1.45rem', lineHeight: 1.2 }}>
            {submitted ? 'Submission sent' : 'Ready to review'}
          </Typography>
          <Typography variant="body2" sx={{ color: tok('onSurfaceMuted'), mt: 0.5 }}>
            {title || (submitted ? 'A copy of your answers' : 'Check the details before sending')}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ borderTop: `1px solid ${tok('cardBorder')}`, pt: 2, display: 'grid', gap: 2 }}>
        {entries.length === 0 && <Typography variant="body2" sx={{ color: tok('onSurfaceMuted') }}>
          {submitted ? 'Your submission has been sent.' : 'Open the review to check your answers.'}
        </Typography>}
        {(expanded ? entries : entries.slice(0, 3)).map(entry => (
          <Box key={entry.id}>
            <Typography variant="caption" sx={{ display: 'block', color: tok('onSurfaceMuted'), mb: 0.5 }}>{entry.question}</Typography>
            {entry.answer.startsWith('https://firebasestorage.googleapis.com/') ? (
              <Box component="button" type="button" aria-label={`View photo for ${entry.question}`}
                onClick={event => { event.stopPropagation(); setPhoto(entry.answer); }}
                sx={{ p: 0, border: 0, cursor: 'pointer', borderRadius: '14px', overflow: 'hidden', bgcolor: tok('surface') }}>
                <Box component="img" src={entry.answer} alt={entry.question} sx={{ display: 'block', width: 112, height: 88, objectFit: 'cover' }} />
              </Box>
            ) : <Typography sx={{ fontSize: '1rem', lineHeight: 1.6, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{entry.answer.trim() || 'Not provided'}</Typography>}
          </Box>
        ))}
      </Box>
      {entries.length > 3 && <Button fullWidth aria-expanded={expanded}
        onClick={event => { event.stopPropagation(); setExpanded(!expanded); }}
        sx={{ mt: 1.5, justifyContent: 'space-between', minHeight: 44, color: tok('onSurface'), textTransform: 'none' }}>
        {expanded ? 'Show fewer details' : `View all ${entries.length} answers`}
        <Box component="span" aria-hidden="true">{expanded ? '−' : '+'}</Box>
      </Button>}
      {submitted && onRequestAttention && <Box sx={{ mt: 2.25, pt: 2, borderTop: `1px solid ${tok('cardBorder')}` }}>
        <Button fullWidth disabled={busy} onClick={onRequestAttention} startIcon={<NotificationsNoneIcon />}
          sx={{ minHeight: 44, borderRadius: 99, bgcolor: tok('surface'), color: tok('onSurface'), textTransform: 'none' }}>
          {busy ? 'Sending request…' : 'Request attention'}
        </Button>
      </Box>}
      <Dialog open={!!photo} onClose={() => setPhoto(null)} onClick={event => event.stopPropagation()}>
        <IconButton aria-label="Close photo" onClick={() => setPhoto(null)} sx={{ alignSelf: 'flex-end' }}><CloseIcon /></IconButton>
        {photo && <Box component="img" src={photo} alt="Submitted attachment" sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />}
      </Dialog>
    </Paper>
  );
}
